import { useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { filterProvinces } from "@/lib/filterData";
import { Check, Search } from "lucide-react";
import { cn } from "@/lib/utils";

export type AccountTypeFilter = "all" | "company" | "personal";
export type VerifiedFilter = "all" | "verified";

export interface MemberFilters {
  accountType: AccountTypeFilter;
  verified: VerifiedFilter;
  province: string | null;
  municipality: string | null;
}

export const defaultFilters: MemberFilters = {
  accountType: "all",
  verified: "all",
  province: null,
  municipality: null,
};

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  initial: MemberFilters;
  onApply: (f: MemberFilters) => void;
}

function RadioRow({
  options,
  value,
  onChange,
}: {
  options: { value: string; label: string }[];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={cn(
            "rounded-full border px-3 py-1.5 text-sm transition-colors",
            value === o.value
              ? "border-primary bg-primary text-primary-foreground"
              : "border-border bg-background text-foreground hover:bg-secondary"
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function SearchablePicker({
  items,
  selected,
  onSelect,
  placeholder,
  emptyLabel,
}: {
  items: string[];
  selected: string | null;
  onSelect: (v: string | null) => void;
  placeholder: string;
  emptyLabel: string;
}) {
  const [q, setQ] = useState("");
  const filtered = useMemo(
    () => items.filter((i) => i.toLowerCase().includes(q.toLowerCase())).slice(0, 200),
    [items, q]
  );
  return (
    <div className="rounded-md border border-border">
      <div className="relative border-b border-border">
        <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={placeholder}
          className="h-8 border-0 pl-7 text-sm focus-visible:ring-0"
        />
      </div>
      <ScrollArea className="h-40">
        <div className="p-1">
          <button
            type="button"
            onClick={() => onSelect(null)}
            className={cn(
              "flex w-full items-center justify-between rounded-md px-2 py-1.5 text-left text-sm hover:bg-secondary",
              selected === null && "bg-secondary"
            )}
          >
            <span>{emptyLabel}</span>
            {selected === null && <Check className="h-3.5 w-3.5" />}
          </button>
          {filtered.map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => onSelect(item)}
              className={cn(
                "flex w-full items-center justify-between rounded-md px-2 py-1.5 text-left text-sm hover:bg-secondary",
                selected === item && "bg-secondary"
              )}
            >
              <span>{item}</span>
              {selected === item && <Check className="h-3.5 w-3.5" />}
            </button>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}

export default function MemberFilterModal({ open, onOpenChange, initial, onApply }: Props) {
  const [draft, setDraft] = useState<MemberFilters>(initial);

  // Reset draft when re-opened
  const handleOpenChange = (o: boolean) => {
    if (o) setDraft(initial);
    onOpenChange(o);
  };

  const provinceList = filterProvinces.map((p) => p.name);
  const kommunList = useMemo(() => {
    if (!draft.province) return filterProvinces.flatMap((p) => p.municipalities);
    const found = filterProvinces.find((p) => p.name === draft.province);
    return found ? found.municipalities : [];
  }, [draft.province]);

  const reset = () => setDraft(defaultFilters);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Filtrera medlemmar</DialogTitle>
        </DialogHeader>
        <div className="space-y-5 py-2">
          <div className="space-y-2">
            <Label className="text-sm">Kontotyp</Label>
            <RadioRow
              value={draft.accountType}
              onChange={(v) => setDraft({ ...draft, accountType: v as AccountTypeFilter })}
              options={[
                { value: "all", label: "Alla" },
                { value: "company", label: "Företag" },
                { value: "personal", label: "Privatperson" },
              ]}
            />
          </div>

          <div className="space-y-2">
            <Label className="text-sm">Verifierad</Label>
            <RadioRow
              value={draft.verified}
              onChange={(v) => setDraft({ ...draft, verified: v as VerifiedFilter })}
              options={[
                { value: "all", label: "Alla" },
                { value: "verified", label: "Endast verifierade" },
              ]}
            />
          </div>

          <div className="space-y-2">
            <Label className="text-sm">Län (landskap)</Label>
            <SearchablePicker
              items={provinceList}
              selected={draft.province}
              onSelect={(v) => setDraft({ ...draft, province: v, municipality: null })}
              placeholder="Sök landskap..."
              emptyLabel="Alla landskap"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-sm">Kommun</Label>
            <SearchablePicker
              items={kommunList}
              selected={draft.municipality}
              onSelect={(v) => setDraft({ ...draft, municipality: v })}
              placeholder="Sök kommun..."
              emptyLabel="Alla kommuner"
            />
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 pt-2">
          <button
            type="button"
            onClick={reset}
            className="text-sm text-muted-foreground underline-offset-4 hover:underline"
          >
            Rensa filter
          </button>
          <Button
            onClick={() => {
              onApply(draft);
              onOpenChange(false);
            }}
          >
            Visa resultat
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
